import { useEffect, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { Button } from '@localagent/ui/components/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@localagent/ui/components/dialog';
import { Label } from '@localagent/ui/components/label';
import { getSDK } from '../../services/sdk-instance';
import { holdsIdentityAtom } from '../../stores/auth/atoms';
import { sdkConfiguredAtom } from '../../stores/sdk/atoms';
import {
  useDeleteCatalogEntry,
  useEntryReferences,
  useProviders,
  useReassignEntryReferences,
} from '../../hooks/useProviderManagement';
import type { ListProviderModelsResponse, ModelCatalogEntry, ProviderType } from '@localagent/sdk';

export interface DeleteEntryDialogProps {
  providerType: ProviderType;
  entry: ModelCatalogEntry | null;
  open: boolean;
  onClose: () => void;
  onDeleted?: () => void;
}

interface ReassignTarget {
  provider_type: ProviderType;
  name: string;
  label: string;
}

export function DeleteEntryDialog({
  providerType,
  entry,
  open,
  onClose,
  onDeleted,
}: DeleteEntryDialogProps) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  const [targetValue, setTargetValue] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isWorking, setIsWorking] = useState(false);

  const referencesQuery = useEntryReferences(providerType, entry?.name ?? null, open);
  const references = referencesQuery.data?.references;
  const hasReferences = (references?.length ?? 0) > 0;

  const providersQuery = useProviders();
  const providers = providersQuery.data?.providers;

  // Reassign target candidates span every provider's catalog. Fetched via
  // useQueries (one query per provider, sharing the ['provider-models', p]
  // cache keys) only once references exist.
  const modelQueries = useQueries({
    queries: (hasReferences ? providers ?? [] : []).map((p) => ({
      queryKey: ['provider-models', p.provider_type],
      queryFn: (): Promise<ListProviderModelsResponse> =>
        getSDK().persona.listProviderModels(p.provider_type),
      enabled: Boolean(isAuthenticated && sdkConfigured && open),
    })),
  });

  const targets = useMemo<ReassignTarget[]>(() => {
    if (!hasReferences || !providers) return [];
    const result: ReassignTarget[] = [];
    providers.forEach((p, i) => {
      const models = modelQueries[i]?.data?.models;
      if (!models) return;
      for (const m of models) {
        // Exclude the entry being deleted.
        if (p.provider_type === providerType && m.name === entry?.name) continue;
        result.push({
          provider_type: p.provider_type,
          name: m.name,
          label: m.display_name || m.name,
        });
      }
    });
    return result;
  }, [hasReferences, providers, modelQueries, providerType, entry]);

  const deleteMutation = useDeleteCatalogEntry();
  const reassignMutation = useReassignEntryReferences();

  useEffect(() => {
    if (open) {
      setTargetValue('');
      setErrorMessage(null);
      setIsWorking(false);
    }
  }, [open]);

  const handleDelete = async () => {
    if (!entry) return;
    setErrorMessage(null);
    setIsWorking(true);
    try {
      await deleteMutation.mutateAsync({ providerType, name: entry.name });
      onDeleted?.();
      onClose();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setIsWorking(false);
    }
  };

  const handleReassignAndDelete = async () => {
    if (!entry || !targetValue) return;
    const target = JSON.parse(targetValue) as { p: ProviderType; n: string };
    setErrorMessage(null);
    setIsWorking(true);
    try {
      await reassignMutation.mutateAsync({
        providerType,
        params: {
          from_name: entry.name,
          to_provider_type: target.p,
          to_name: target.n,
        },
      });
      await deleteMutation.mutateAsync({ providerType, name: entry.name });
      onDeleted?.();
      onClose();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setIsWorking(false);
    }
  };

  const groupedTargets = useMemo(() => {
    const groups = new Map<string, ReassignTarget[]>();
    for (const t of targets) {
      const list = groups.get(t.provider_type) ?? [];
      list.push(t);
      groups.set(t.provider_type, list);
    }
    return Array.from(groups.entries());
  }, [targets]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Delete Model Entry</DialogTitle>
          <DialogDescription>
            Delete &quot;{entry?.display_name || entry?.name}&quot; ({entry?.name})?
            {entry?.origin === 'synced' && ' Sync will restore this entry.'}
          </DialogDescription>
        </DialogHeader>

        {referencesQuery.isLoading && (
          <p className="text-label text-muted-foreground py-2">Checking references...</p>
        )}
        {referencesQuery.error && (
          <p className="text-label text-destructive py-2">
            Failed to load references: {referencesQuery.error.message}
          </p>
        )}

        {references && hasReferences && (
          <div className="flex flex-col gap-3 py-2">
            <div data-testid="delete-entry-references">
              <p className="text-label font-medium mb-1">
                {references.length} agent{references.length !== 1 ? 's' : ''} reference{references.length === 1 ? 's' : ''} this entry:
              </p>
              <ul className="text-body text-muted-foreground list-disc pl-5">
                {references.map((ref) => (
                  <li key={ref.persona_id}>{ref.persona_name}</li>
                ))}
              </ul>
            </div>
            <div>
              <Label htmlFor="delete-entry-reassign-target">Reassign agents to</Label>
              <select
                id="delete-entry-reassign-target"
                data-testid="delete-entry-reassign-target"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                className="w-full rounded-md bg-muted px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select a model entry...</option>
                {groupedTargets.map(([pt, list]) => (
                  <optgroup key={pt} label={pt}>
                    {list.map((t) => (
                      <option key={`${t.provider_type}:${t.name}`} value={JSON.stringify({ p: t.provider_type, n: t.name })}>
                        {t.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <p className="text-caption text-muted-foreground">
              Reassignment swaps each agent&apos;s full model configuration — provider,
              model, and completion parameters are re-derived from the target
              entry&apos;s defaults.
            </p>
          </div>
        )}

        {errorMessage && (
          <p className="text-body text-destructive" data-testid="provider-dialog-error">{errorMessage}</p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {references && hasReferences ? (
            <Button
              variant="destructive"
              data-testid="delete-entry-confirm"
              onClick={() => void handleReassignAndDelete()}
              disabled={!targetValue || isWorking}
            >
              {isWorking ? 'Working...' : 'Reassign all & delete'}
            </Button>
          ) : (
            <Button
              variant="destructive"
              data-testid="delete-entry-confirm"
              onClick={() => void handleDelete()}
              disabled={!references || isWorking}
            >
              {isWorking ? 'Deleting...' : 'Delete'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
